import { ClientMode } from "../../constants";

const inModes = (mode, modes) => modes.includes(mode);

/**
 * Determines which tabs should be visible based on the current mode configuration.
 * This centralizes all visibility logic to make it easier to understand and maintain.
 *
 * @param {Object} params
 * @param {string} params.mode - Current client mode
 * @param {boolean} params.showCapturePatientTab - Temp Feature flag to enable Patients tab in CAPTURE mode
 *
 * @returns {Object} visibility flags
 * @returns {boolean} returns.sentReferrals
 * @returns {boolean} returns.receivedReferrals
 * @returns {boolean} returns.searchReferrals
 * @returns {boolean} returns.practiceProviders
 * @returns {boolean} returns.documentTriage
 * @returns {boolean} returns.serviceRequests
 * @returns {boolean} returns.patients
 * @returns {boolean} returns.sentFaxes
 * @returns {boolean} returns.analytics
 * @returns {boolean} returns.admin
 */
const determineTabVisibility = ({ mode, showCapturePatientTab }) => {
  return {
    // Referral management tabs - EXCHANGE / FULL only
    sentReferrals: inModes(mode, [ClientMode.EXCHANGE, ClientMode.FULL]),
    receivedReferrals: inModes(mode, [ClientMode.EXCHANGE, ClientMode.FULL]),
    searchReferrals: inModes(mode, [ClientMode.EXCHANGE, ClientMode.FULL]),
    practiceProviders: inModes(mode, [ClientMode.EXCHANGE, ClientMode.FULL]),

    // Document triage - CAPTURE / EXCHANGE / FULL
    documentTriage: inModes(mode, [
      ClientMode.CAPTURE,
      ClientMode.EXCHANGE,
      ClientMode.FULL,
    ]),

    // Service requests - CAPTURE / FULL
    serviceRequests: inModes(mode, [ClientMode.CAPTURE, ClientMode.FULL]),

    // Patients - EXCHANGE / FULL, OR CAPTURE when explicitly enabled
    // TODO: remove showCapturePatientTab condition when feature flag is removed
    patients:
      inModes(mode, [ClientMode.EXCHANGE, ClientMode.FULL]) ||
      (mode === ClientMode.CAPTURE && showCapturePatientTab),

    // Sent faxes - CAPTURE / EXCHANGE / FULL (NOT network-only)
    sentFaxes: inModes(mode, [
      ClientMode.CAPTURE,
      ClientMode.EXCHANGE,
      ClientMode.FULL,
    ]),

    // Analytics dropdown is available in all modes
    analytics: inModes(mode, [
      ClientMode.CAPTURE,
      ClientMode.EXCHANGE,
      ClientMode.FULL,
      ClientMode.NETWORK_REPORTS_ONLY,
    ]),

    // Admin dropdown - CAPTURE / EXCHANGE / FULL (NOT network-only)
    admin: inModes(mode, [
      ClientMode.CAPTURE,
      ClientMode.EXCHANGE,
      ClientMode.FULL,
    ]),
  };
};

/**
 * Determines admin sub-items visibility based on client mode.
 *
 * @param {Object} params
 * @param {string} params.mode - Current client mode (one of ClientMode.*)
 * @param {boolean} params.showProvidersMgmtTab - Feature flag to enable Providers management in CAPTURE mode
 *
 * @returns {Object} visibility flags
 * @returns {boolean} returns.insurancePlans
 * @returns {boolean} returns.userProvisioning
 * @returns {boolean} returns.statusCustomization
 * @returns {boolean} returns.notificationsCustomization
 * @returns {boolean} returns.srNameCustomization
 * @returns {boolean} returns.srPresetMessages
 * @returns {boolean} returns.tenantLogoUpload
 * @returns {boolean} returns.srProviderMgmt
 */
const determineAdminItemsVisibility = ({ mode, showProvidersMgmtTab }) => {
  return {
    insurancePlans: true,
    userProvisioning: true,
    statusCustomization: true,
    notificationsCustomization: true,
    srNameCustomization: true,
    srPresetMessages: true,
    tenantLogoUpload: true,
    // Provider management only visible in capture mode when explicitly enabled
    srProviderMgmt: mode === ClientMode.CAPTURE && showProvidersMgmtTab,
  };
};

/**
 * Determines which menu options should be displayed in the sidebar based on
 * authentication status, user roles, current system mode and feature flags
 *
 * @param {boolean} authenticated - Whether the user is authenticated
 * @param {Object} roles - User roles
 * @param {boolean} roles.adminTab - Whether user has admin tab access
 * @param {Object} reportDropdownState
 * @param {boolean} reportDropdownState.openReportDropdown
 * @param {Function} reportDropdownState.setOpenReportDropdown
 * @param {Object} adminDropdownState
 * @param {boolean} adminDropdownState.openAdminDropdown
 * @param {Function} adminDropdownState.setOpenAdminDropdown
 * @param {string} clientMode - Current client mode
 * @param {Array<Object>} dashboardLists - Available dashboards
 * @param {string} dashboardLists[].id
 * @param {string} dashboardLists[].qsDashboardId
 * @param {string} dashboardLists[].name
 * @param {boolean} dashboardLists[].isMemberOnly
 * @param {boolean} [showProvidersMgmtTab=false] - Feature flag for Providers management (Admin)
 * @param {boolean} [showCapturePatientTab=false] - Feature flag for Patients tab in CAPTURE mode
 *
 * @returns {Array<Object>} Menu option objects
 */
export const determineSideMenuOptions = (
  authenticated,
  roles,
  { openReportDropdown: reportState, setOpenReportDropdown: reportOnClick },
  { openAdminDropdown: adminState, setOpenAdminDropdown: adminOnClick },
  clientMode,
  dashboardLists,
  showProvidersMgmtTab = false,
  showCapturePatientTab = false
) => {
  const options = [];

  // Home is always visible
  options.push({ id: "menu_home", text: "Home", route: "/" });

  if (!authenticated) {
    return options;
  }

  // Determine which tabs should be visible based on current mode
  const tabVisibility = determineTabVisibility({
    mode: clientMode,
    showCapturePatientTab,
  });

  // Referral management tabs
  if (tabVisibility.sentReferrals) {
    options.push({
      id: "menu_sent_referrals",
      text: "Sent Referrals",
      route: "/referrals/sent",
    });
  }
  if (tabVisibility.receivedReferrals) {
    options.push({
      id: "menu_received_referrals",
      text: "Received Referrals",
      route: "/referrals/received",
    });
  }
  if (tabVisibility.searchReferrals) {
    options.push({
      id: "menu_search_referrals_facets",
      text: "Search Referrals",
      route: "/referrals/search-facets",
    });
  }
  if (tabVisibility.practiceProviders) {
    options.push({
      id: "menu_search_practice_providers",
      text: "Practice/Providers",
      route: "/providers/search",
    });
  }

  // Document management
  if (tabVisibility.documentTriage) {
    options.push({
      id: "menu_document_triage",
      text: "Document Triage",
      route: "/documents",
    });
  }

  // Service requests
  if (tabVisibility.serviceRequests) {
    options.push({
      id: "menu_service_request",
      text: "Service Requests",
      route: "/service-requests/",
    });
  }

  // Patient search
  if (tabVisibility.patients) {
    options.push({
      id: "menu_search_patients",
      text: "Patients",
      route: "/patients/search",
    });
  }

  // Fax management
  if (tabVisibility.sentFaxes) {
    options.push({
      id: "menu_sent_faxes",
      text: "Sent Faxes",
      route: "/sent-faxes/",
    });
  }

  // Analytics/Reports dropdown
  if (tabVisibility.analytics && dashboardLists?.length > 0) {
    const filteredDashboards =
      clientMode === ClientMode.NETWORK_REPORTS_ONLY
        ? dashboardLists.filter((d) => !d?.isMemberOnly)
        : dashboardLists;

    options.push({
      id: "menu_analytics",
      text: "Analytics",
      nested: true,
      list: filteredDashboards.map((d) => ({
        id: d.qsDashboardId,
        text: d.name,
        route: `/reports/${d.id}`,
      })),
      stateVariable: reportState,
      onClickFn: reportOnClick,
    });
  }

  // Admin dropdown
  if (roles?.adminTab && tabVisibility.admin) {
    const adminItemsVisibility = determineAdminItemsVisibility({
      mode: clientMode,
      showProvidersMgmtTab,
    });

    const adminItems = [
      {
        id: "insurance_plans",
        text: "Insurance Plans",
        route: "/admin/hermes-tenant-admin-ui",
        visible: adminItemsVisibility.insurancePlans,
      },
      {
        id: "user_provisioning",
        text: "User Provisioning",
        route: "/admin/hermes-tenant-admin-ui-user-provisioning",
        visible: adminItemsVisibility.userProvisioning,
      },
      {
        id: "status_customization",
        text: "Status Customization",
        route: "/admin/status-customization",
        visible: adminItemsVisibility.statusCustomization,
      },
      {
        id: "notifications_customization",
        text: "Notifications Customization",
        route: "/admin/notifications-customization",
        visible: adminItemsVisibility.notificationsCustomization,
      },
      {
        id: "sr_name_customization",
        text: "Service Request Names",
        route: "/admin/hermes-tenant-admin-sr-customization",
        visible: adminItemsVisibility.srNameCustomization,
      },
      {
        id: "sr_preset_messages",
        text: "Preset Messages",
        route: "/admin/hermes-tenant-admin-preset-messages",
        visible: adminItemsVisibility.srPresetMessages,
      },
      {
        id: "tenant_logo_upload",
        text: "Logo Upload",
        route: "/admin/hermes-tenant-admin-logo-upload",
        visible: adminItemsVisibility.tenantLogoUpload,
      },
      {
        id: "sr_provider_mgmt",
        text: "Providers",
        route: "/admin/hermes-tenant-admin-providers-mgmt",
        visible: adminItemsVisibility.srProviderMgmt,
      },
    ];

    const visibleAdminItems = adminItems
      .filter((i) => i.visible)
      .map(({ visible, ...i }) => i);

    if (visibleAdminItems.length > 0) {
      options.push({
        id: "menu_admin",
        text: "Admin",
        nested: true,
        list: visibleAdminItems,
        stateVariable: adminState,
        onClickFn: adminOnClick,
      });
    }
  }

  return options;
};
